import { defineStore } from "pinia"
import { HEAVYBIT_ORG_ID, type Principal } from "@auger/identity"
import { isViewSettingEntry, type ViewSettingEntry } from "@auger/ui/composables/viewSettings/entry"
import type { FetchSelfQuery } from "@/gql/graphql"
import { client, DEV_BYPASS_ROLE } from "@/init/apollo"
import { getUserById } from "@/stores/users"

interface UserRecord {
  email: string | null
  name: string | null
  partner: boolean
  record_id: number | null
  record: FetchSelfQuery["person"]
}

interface UserData {
  fireflies_token?: string | null
  pipedrive_token?: string | null
  airtable_token?: string | null
  default_landing_path?: string | null
  [key: string]: unknown
}

interface UserState {
  authenticated: boolean
  loggingOut: boolean
  /** The signed-in principal, read from the session JWT: its identity, session and permissions. */
  principal: Principal | null
  user: UserRecord
  userData: UserData | null
  viewSettings: Record<string, ViewSettingEntry>
}

/** The signed-in person's record id: the principal's auger_person_id. */
export const getPersonId = (): number | null =>
  useUserStore().principal?.identity.auger_person_id ?? null

/** The signed-in person's record id. Kept as the name callers know; it is
 *  the person id from the session, no longer users.record_id. */
export const getUserRecordId = (): number | null =>
  getPersonId() ?? useUserStore().user?.record_id ?? null

/**
 * Whether the signed-in member owns a row: its owner pair is this person in
 * the Heavybit organization, the same test as the Hasura owner rule. The app
 * signs in staff only, so the session's org is always Heavybit's.
 */
export const isOwnedByMe = (
  row: { owner_person_id?: number | null; owner_org_id?: number | null } | null | undefined
): boolean => {
  const personId = getUserRecordId()
  return (
    personId != null && row?.owner_person_id === personId && row?.owner_org_id === HEAVYBIT_ORG_ID
  )
}

export const isLoggedIn = (): boolean => !!useUserStore().principal

export const useUserStore = defineStore("user", {
  state: (): UserState => ({
    authenticated: false,
    loggingOut: false,
    principal: null,
    user: {
      email: null,
      name: null,
      partner: false,
      record_id: null,
      record: null,
    },
    userData: null,
    viewSettings: {},
  }),

  getters: {
    hadAuthenticatedSession: (state): boolean => state.authenticated || state.loggingOut,
    email: (state): string | null | undefined => state.user?.email,
    name: (state): string | null | undefined => state.user?.name,
    // Admin is heavybit_admin among the session's roles, never users.admin.
    isAdmin: (state): boolean =>
      state.principal?.permissions.roles.includes("heavybit_admin") ?? false,
    // A partner is a person on the partners list (the heavybit_partner role).
    isPartner: (state): boolean =>
      !!getUserById(state.principal?.identity.auger_person_id ?? null)?.partner,
    firefliesReady: (state): boolean => !!state.userData?.fireflies_token,
    firefliesToken: (state) => state.userData?.fireflies_token,
    pipedriveReady: (state): boolean => !!state.userData?.pipedrive_token,
    pipedriveToken: (state) => state.userData?.pipedrive_token,
    airtableReady: (state): boolean => !!state.userData?.airtable_token,
    airtableToken: (state) => state.userData?.airtable_token,
  },

  actions: {
    // The signed-in principal, or null when signed out.
    setPrincipal(principal: Principal | null) {
      this.authenticated = principal !== null
      this.principal = principal
    },

    updateProperties(payload: Partial<UserRecord>) {
      this.$patch((state) => {
        Object.assign(state.user, payload)
      })
    },

    updateUserData(data: UserData | null) {
      this.userData = data
    },

    // `all` comes straight off the network (the login fetch) — filter out
    // anything that doesn't match the envelope shape rather than trusting a
    // malformed row into every `read()` call site's cast downstream.
    updateViewSettings(all: Record<string, unknown> | null | undefined) {
      const entries = Object.entries(all ?? {})
      const valid = entries.filter(([, entry]) => isViewSettingEntry(entry))
      if (valid.length < entries.length) {
        const droppedKeys = entries
          .filter(([, entry]) => !isViewSettingEntry(entry))
          .map(([k]) => k)
        // A real server-side corruption should leave a trace — silently
        // dropping it is the "reported months later, no diagnostic trail"
        // failure mode this guard exists to prevent for read()'s callers,
        // not to also become for whoever investigates the report.
        console.error("Dropping malformed user_data.view_settings entries:", droppedKeys)
      }
      this.viewSettings = Object.fromEntries(valid) as Record<string, ViewSettingEntry>
    },

    setViewSetting(key: string, entry: ViewSettingEntry) {
      this.viewSettings = { ...this.viewSettings, [key]: entry }
    },

    removeViewSetting(key: string) {
      const { [key]: _, ...rest } = this.viewSettings
      this.viewSettings = rest
    },

    // The dev bypass has no token to read a principal from: it is a staff
    // member in the Heavybit organization.
    setForcedUser(id: string, personId?: string | null) {
      this.authenticated = true
      const person = Number(personId)
      this.principal = {
        identity: {
          stytch_member_id: id,
          auger_person_id: Number.isInteger(person) && person > 0 ? person : undefined,
          auger_organization_id: HEAVYBIT_ORG_ID,
        },
        permissions: {
          roles: [DEV_BYPASS_ROLE],
          defaultRole: DEV_BYPASS_ROLE,
          scopes: [],
          isScoped: false,
        },
      }
    },

    startLogout() {
      this.loggingOut = true
      this.userData = null
      this.viewSettings = {}
      client.clearStore().catch((err: Error) => {
        console.error("Failed to clear Apollo cache on logout:", err)
      })
    },
  },
})
